import axiosInstance from '@/request/axios'
import {
  CpnApiResponse,
  CpnBeneficiaryUser,
  CpnConfigurationResponse,
  CpnPaymentsResponse,
  CpnQuote,
  CpnQuoteResponse,
  CpnRequirementsResponse,
  CpnRfis,
  CpnStatusResponse,
  CpnSupportTicketRequest,
  CpnSupportTicketResponse,
  CpnSwapStatusResponse,
  CpnTransactions,
  SwapPaymentRequest,
  SwapPaymentResponse,
  SwapQuoteRequest,
  SwapQuoteResponse,
} from '@/types/cpn'
import { CpnPaymentReason } from '@/utils/cpn'

type CpnBeneficiaryApiResponse = {
  beneficiaryUuid?: string
  uuid?: string
  firstName: string
  lastName: string
  street?: string
  city?: string
  stateProvince?: string
  postalCode?: string
  country?: string
  metadata?: Record<string, unknown>
  isBusiness?: boolean
  isSelf?: boolean
  paymentReason: CpnPaymentReason
}

// Quote Management
export const cpnService = {
  // Get latest quote (cached)
  async getLatestQuote(params: {
    quoteType: 'fromExact' | 'toExact'
    blockchain?: string
    senderCountry?: string
    sourceCurrency?: string
    amount: string
    destinationCountry: string
    destinationCurrency: string
    paymentMethod?: string
  }): Promise<CpnQuoteResponse> {
    try {
      // Filter out undefined values for cleaner URL
      const filteredParams = Object.fromEntries(
        Object.entries({ ...params, recipientType: 'INDIVIDUAL' }).filter(
          ([_, value]) => value !== undefined
        )
      )
      const queryParams = new URLSearchParams(filteredParams).toString()
      const fullUrl = `/cpn/latest-quote?${queryParams}`

      const response = await axiosInstance.get(fullUrl, {
        // Disable axios-retry for this request to get clean error messages
        'axios-retry': { retries: 0 },
      })
      return response.data
    } catch (error) {
      // Extract detailed error message from API response
      let errorMessage = 'Failed to get quote'
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error?.response?.data) {
        // If data is a string, use it directly
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data
        }
      } else if (error instanceof Error) {
        errorMessage = error.message
      }

      // Create a new error with the extracted message
      const enhancedError = new Error(errorMessage) as Error & {
        response?: any
      }
      enhancedError.response = error?.response
      throw enhancedError
    }
  },
  // Get quote by ID
  async getQuote(quoteId: string): Promise<{ quote: CpnQuote }> {
    const response = await axiosInstance.get(`/cpn/quote?quote_id=${quoteId}`)
    return response.data
  },

  async getLatestCadQuote(params: {
    quoteType: 'fromExact' | 'toExact'
    blockchain: string
    amount: string
    destinationCountry: string
    destinationCurrency: string
    paymentMethod?: string
  }): Promise<{
    quotes: Array<{
      id: string
      blockchain: string
      sourceAmount: { amount: string; currency: string }
      destinationAmount: { amount: string; currency: string }
      exchangeRate: { rate: string; pair: string }
      fees: { totalAmount: { amount: string; currency: string } }
      paymentMethodType: string
      createDate: string
      quoteExpireDate: string
    }>
    cachedQuotes: boolean
    cadAmount: number
    fee: number
  }> {
    const queryParams = new URLSearchParams({
      ...params,
      recipientType: 'INDIVIDUAL',
    }).toString()
    const response = await axiosInstance.get(
      `/cpn/latest-quote/cad?${queryParams}`
    )
    return response.data
  },

  // Beneficiary Management
  async createBeneficiaryUser(beneficiaryData: {
    firstName: string
    lastName: string
    street: string
    city: string
    stateProvince: string
    postalCode: string
    country: string
    isBusiness: boolean
    isSelf: boolean
    paymentReason: string
  }): Promise<{ beneficiaryUuid: string; message: string; status: string }> {
    const response = await axiosInstance.post(
      '/cpn/beneficiary-user',
      beneficiaryData
    )
    return response.data
  },

  async getBeneficiaryUser(
    beneficiaryId: string
  ): Promise<{ beneficiaryUser: CpnBeneficiaryUser }> {
    const response = await axiosInstance.get(
      `/cpn/beneficiary-user?beneficiary_id=${beneficiaryId}`
    )
    const data = response.data

    // Transform API response to match CpnBeneficiaryUser interface
    if (data.beneficiaryUser) {
      const beneficiary: CpnBeneficiaryApiResponse = data.beneficiaryUser
      data.beneficiaryUser = {
        uuid: beneficiary.beneficiaryUuid || beneficiary.uuid || '',
        firstName: beneficiary.firstName,
        lastName: beneficiary.lastName,
        street: beneficiary.street,
        city: beneficiary.city,
        stateProvince: beneficiary.stateProvince,
        postalCode: beneficiary.postalCode,
        country: beneficiary.country,
        metadata: beneficiary.metadata,
        isBusiness: beneficiary.isBusiness ?? false,
        isSelf: beneficiary.isSelf ?? false,
        paymentReason: beneficiary.paymentReason,
      }
    }

    return data
  },

  async getBeneficiaryUsers(): Promise<{
    beneficiaryUsers: CpnBeneficiaryUser[]
  }> {
    const response = await axiosInstance.get('/cpn/beneficiary-users')
    const data = response.data

    // Transform API response to match CpnBeneficiaryUser interface
    if (data.beneficiaryUsers && Array.isArray(data.beneficiaryUsers)) {
      data.beneficiaryUsers = data.beneficiaryUsers.map(
        (beneficiary: CpnBeneficiaryApiResponse): CpnBeneficiaryUser => {
          // Map beneficiaryUuid to uuid for backward compatibility
          return {
            uuid: beneficiary.beneficiaryUuid || beneficiary.uuid || '',
            firstName: beneficiary.firstName,
            lastName: beneficiary.lastName,
            street: beneficiary.street,
            city: beneficiary.city,
            stateProvince: beneficiary.stateProvince,
            postalCode: beneficiary.postalCode,
            country: beneficiary.country,
            metadata: beneficiary.metadata,
            isBusiness: beneficiary.isBusiness ?? false,
            isSelf: beneficiary.isSelf ?? false,
            paymentReason: beneficiary.paymentReason,
          }
        }
      )
    }

    return data
  },

  async updateBeneficiaryMetadata(
    beneficiaryId: string,
    metadata: Record<string, unknown>
  ): Promise<CpnApiResponse<null>> {
    const response = await axiosInstance.put(
      `/cpn/beneficiary-user?beneficiary_id=${beneficiaryId}`,
      { metadata }
    )
    return response.data
  },

  // Payment Management
  async createPaymentWithQuote(paymentData: {
    quoteId: string
    beneficiaryRefId: string
    blockchain: string
    magicWord?: string
    reasonForPayment: string
    useCase: string
    rfiMagicWord?: string
  }): Promise<{
    paymentId: string
    quoteId: string
    message: string
    status: string
  }> {
    const response = await axiosInstance.post('/cpn/payment', paymentData)
    return response.data
  },

  async createPayment(paymentData: {
    // For new quote mode
    blockchain?: string
    destinationCountry?: string
    destinationCurrency?: string
    amount?: string
    quoteType?: 'fromExact' | 'toExact'
    paymentMethod?: string
    // For existing quote mode
    quoteId?: string
    // Common fields
    beneficiaryRefId: string
    reasonForPayment: string
    useCase: string
    rfiMagicWord?: string
    refCode?: string
  }): Promise<{ paymentId: string; quoteId?: string }> {
    const response = await axiosInstance.post('/cpn/payment', paymentData)
    return response.data
  },

  async getPayment(paymentId: string): Promise<{ paymentId: string }> {
    const response = await axiosInstance.get(
      `/cpn/payment?payment_id=${paymentId}`
    )
    return response.data
  },

  async getPayments(active?: boolean): Promise<CpnPaymentsResponse> {
    const response = await axiosInstance.get('/cpn/payments', {
      params: { active },
    })
    return response.data
  },

  async getPaymentStatusWithIntentUUID(
    paymentId: string
  ): Promise<CpnStatusResponse> {
    const response = await axiosInstance.get(
      `/cpn/status?intent_id=${paymentId}`
    )
    return response.data
  },

  async getSwapStatus(intentId: string): Promise<CpnSwapStatusResponse> {
    const response = await axiosInstance.get(
      `/cpn/swap-status?intent_id=${intentId}`
    )
    return response.data
  },

  // Payment Requirements
  async getPaymentRequirements(
    quoteId: string,
    beneficiaryUuid: string
  ): Promise<CpnRequirementsResponse> {
    const params = new URLSearchParams({
      quote_id: quoteId,
      beneficiary_uuid: beneficiaryUuid,
    })
    const response = await axiosInstance.get(
      `/cpn/requirements?${params.toString()}`
    )
    return response.data
  },

  // Transactions
  async getTransactions(
    paymentId: string,
    active?: boolean
  ): Promise<{ transactions: CpnTransactions[] }> {
    const params = new URLSearchParams({ payment_id: paymentId })
    if (active !== undefined) {
      params.append('active', active.toString())
    }
    const response = await axiosInstance.get(
      `/cpn/transactions?${params.toString()}`
    )
    return response.data
  },

  // RFI Management
  async getRfi(rfiId: string): Promise<{ rfi: CpnRfis }> {
    const response = await axiosInstance.get(`/cpn/rfi?rfi_id=${rfiId}`)
    return response.data
  },

  async getRfis(): Promise<{ rfis: CpnRfis[] }> {
    const response = await axiosInstance.get('/cpn/rfis')
    return response.data
  },

  async submitRfi(
    rfiId: string,
    paymentId: string,
    rfiData: Array<{
      name: string
      value: unknown
    }>
  ): Promise<CpnApiResponse<null>> {
    const response = await axiosInstance.post(
      `/cpn/rfi?rfi_id=${rfiId}&payment_id=${paymentId}`,
      {
        rfiData,
      }
    )
    return response.data
  },

  // Support Tickets
  async createSupportTicket(
    ticketData: CpnSupportTicketRequest
  ): Promise<CpnSupportTicketResponse> {
    const response = await axiosInstance.post('/cpn/support-ticket', ticketData)
    return response.data
  },

  // New Swap API Methods

  // Step 2: Lock Quote - Create swap quote intent
  async createSwapQuote(params: SwapQuoteRequest): Promise<SwapQuoteResponse> {
    const response = await axiosInstance.post('/cpn/swap-quote', {
      ...params,
      recipientType: 'INDIVIDUAL',
    })
    return response.data
  },

  // Step 3: Execute Payment - Submit swap payment
  async createSwapPayment(
    params: SwapPaymentRequest
  ): Promise<SwapPaymentResponse> {
    const response = await axiosInstance.post('/cpn/swap-payment', params)
    return response.data
  },

  // Get CPN configuration for route validation
  async getConfigurations(params: {
    sourceCurrency: string
    destinationCountry: string
    destinationCurrency: string
  }): Promise<CpnConfigurationResponse> {
    const queryParams = new URLSearchParams({
      source_currency: params.sourceCurrency,
      destination_country: params.destinationCountry,
      destination_currency: params.destinationCurrency,
    })
    const response = await axiosInstance.get(
      `/cpn/configurations?${queryParams.toString()}`
    )
    return response.data
  },

  // Get all route configurations (returns only amount limits in CAD)
  async getAllConfigurations(): Promise<{
    configurations: Array<{
      destinationCountry?: string
      destinationCurrency: string
      paymentMethodType: string
      amountLimit: { max: number; min: number; currency: string }
      lastUpdated: string
    }>
    message: string
    status: string
  }> {
    const response = await axiosInstance.get(
      '/cpn/configurations?source_currency=USDC'
    )
    return response.data
  },
}
